import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, num, formatDate } from "@/lib/format";
import { UnitSelect, rid } from "@/components/UnitSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Inbound() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [supplierId, setSupplierId] = useState("");
  const [trxDate, setTrxDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ _k: rid(), product_id: "", qty: 1, cost_price: 0, unit: "" }]);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/inbound"), api.get("/products"), api.get("/suppliers")]);
    setRows(a.data); setProducts(b.data); setSuppliers(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addItem = () => setItems([...items, { _k: rid(), product_id: "", qty: 1, cost_price: 0, unit: "" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const setItem = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    if (k === "product_id") {
      const p = products.find(p => p.product_id === v);
      if (p) { next[i].cost_price = p.cost_price; next[i].unit = p.unit; }
    }
    setItems(next);
  };
  const total = items.reduce((a, it) => {
    const p = products.find(p => p.product_id === it.product_id);
    const factor = p?.packs?.find(pk => pk.unit === it.unit)?.factor || 1;
    return a + Number(it.qty || 0) * factor * Number(it.cost_price || 0);
  }, 0);

  const save = async () => {
    try {
      const sup = suppliers.find(s => s.supplier_id === supplierId);
      const payload = { supplier_id: supplierId, supplier_name: sup?.name || "", notes, date: trxDate, items: items.filter(i => i.product_id && i.qty > 0) };
      if (editing) {
        await api.put(`/inbound/${editing.inbound_id}`, payload);
        toast.success("Barang masuk diperbarui");
      } else {
        await api.post("/inbound", payload);
        toast.success("Barang masuk tercatat");
      }
      setOpen(false); setEditing(null); load();
      setItems([{ _k: rid(), product_id: "", qty: 1, cost_price: 0, unit: "" }]); setNotes(""); setSupplierId(""); setTrxDate(new Date().toISOString().slice(0, 10));
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const openEditInb = (r) => {
    setEditing(r);
    setSupplierId(r.supplier_id || "");
    setNotes(r.notes || "");
    setTrxDate((r.transaction_date || String(r.date).slice(0, 10)));
    setItems(r.items.map(it => ({ _k: rid(), product_id: it.product_id, qty: it.qty, cost_price: it.cost_price, unit: it.unit })));
    setOpen(true);
  };

  const deleteInb = async (r) => {
    if (!window.confirm(`Hapus transaksi ${r.trx_no}? Stok gudang akan dikurangi kembali.`)) return;
    try { await api.delete(`/inbound/${r.inbound_id}`); toast.success("Transaksi dihapus — stok direversal"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <div data-testid="page-inbound" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Transaksi</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Barang Masuk</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-inbound" onClick={() => setEditing(null)}><Plus className="h-4 w-4 mr-1" />Terima Barang</Button></DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>{editing ? `Ubah ${editing.trx_no}` : "Barang Masuk Gudang"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tanggal Transaksi *</Label>
                <Input type="date" value={trxDate} onChange={(e) => setTrxDate(e.target.value)} required disabled={!!editing} data-testid="input-inbound-date" />
              </div>
              <div>
                <Label>Supplier</Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger data-testid="input-supplier"><SelectValue placeholder="Pilih supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Keterangan</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            <div className="mt-2">
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Detail Barang</div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={it._k} className="grid grid-cols-[1fr_70px_90px_130px_40px] gap-2 items-center">
                    <Select value={it.product_id} onValueChange={(v) => setItem(i, "product_id", v)}>
                      <SelectTrigger data-testid={`row-${i}-product`}><SelectValue placeholder="Pilih barang" /></SelectTrigger>
                      <SelectContent>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`row-${i}-qty`} />
                    <UnitSelect product={products.find(p => p.product_id === it.product_id)} value={it.unit} onChange={(v) => setItem(i, "unit", v)} testid={`row-${i}-unit`} />
                    <Input type="number" value={it.cost_price} onChange={(e) => setItem(i, "cost_price", e.target.value)} data-testid={`row-${i}-cost`} />
                    <Button variant="ghost" size="icon" onClick={() => removeItem(i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addItem} className="mt-2" data-testid="btn-add-row"><Plus className="h-3 w-3 mr-1" />Baris</Button>
            </div>
            <DialogFooter className="items-center">
              <div className="mr-auto text-sm">Total: <span className="font-mono font-bold">{rupiah(total)}</span></div>
              <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-inbound">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <table className="w-full text-sm" data-testid="table-inbound">
          <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
            <tr><th className="py-2 px-4">No. Transaksi</th><th>Tanggal</th><th>Supplier</th><th className="text-right">Total</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.inbound_id} className="border-b">
                <td className="py-2 px-4 font-mono text-xs">{r.trx_no}</td>
                <td>{formatDate(r.date)}</td>
                <td>{r.supplier_name || "-"}</td>
                <td className="text-right font-mono">{rupiah(r.total)}</td>
                <td className="whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => openEditInb(r)} data-testid={`edit-inbound-${r.trx_no}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteInb(r)} data-testid={`delete-inbound-${r.trx_no}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Belum ada transaksi</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
